// /crm/deals/:id — detalhe do deal + edição + ações won/lost (Story 11.4)
// - Detalhe com relações (lead/cliente/saas/plano/responsável) + propostas
// - "Dias no estágio" calculado no frontend (AC10 — helpers de timezone)
// - Edição reusa DealForm (injeta version no submit — optimistic lock)
// - Ações won/lost (lost via LostDealDialog com motivo obrigatório — AC5/AC6)
// - Timeline de atividades (componente 11.8) com as mudanças de estágio automáticas

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Check, Pencil, RefreshCw, X } from 'lucide-react';
import { toast } from 'sonner';

import type {
  CrmDealStageSchema,
  CrmDealStatusSchema,
  UpdateCrmDealDto,
} from '@control-center/shared';
import { formatBR, parseLocalDate } from '@control-center/shared';

import { ActivityTimeline } from '../../../components/crm/ActivityTimeline';
import { DealForm } from '../../../components/crm/DealForm';
import { LostDealDialog } from '../../../components/crm/LostDealDialog';
import { PillButton } from '../../../components/clients/PillButton';
import { daysInStage, formatDealMoney } from '../../../lib/deal-helpers';
import { useCrmDeal, useUpdateCrmDeal, type UpdateCrmDealArgs } from '../../../hooks/use-crm-deals';
import { useSaasProducts } from '../../../hooks/use-saas-products';
import { usePlans } from '../../../hooks/use-plans';
import { useUsers } from '../../../hooks/use-users';
import { useCrmPermissions } from '../../../hooks/use-crm-permissions';
import { extractErrorMessage } from '../../../lib/errors';
import type { CrmDealDetailEntity } from '../../../lib/crm-deals-api';

const STAGE_LABEL: Record<CrmDealStageSchema, string> = {
  novo_lead: 'Novo lead',
  qualificacao: 'Qualificação',
  diagnostico: 'Diagnóstico',
  proposta_enviada: 'Proposta enviada',
  negociacao: 'Negociação',
  fechamento_ganho: 'Fechamento ganho',
  fechamento_perdido: 'Fechamento perdido',
};

const STATUS_LABEL: Record<CrmDealStatusSchema, string> = {
  open: 'Em aberto',
  won: 'Ganho',
  lost: 'Perdido',
};

const STATUS_BADGE: Record<CrmDealStatusSchema, string> = {
  open: 'border-line text-muted',
  won: 'border-good/40 text-good',
  lost: 'border-bad/40 text-bad',
};

export function CrmDealDetailPage(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { canWrite } = useCrmPermissions();

  const { data: deal, isLoading, isError, error, refetch, isFetching } = useCrmDeal(id);
  const { data: saasProducts } = useSaasProducts({ limit: 100 });
  const { data: plans } = usePlans({ limit: 100 });
  const { data: responsibleUsers } = useUsers();

  const [isEditing, setIsEditing] = useState(false);
  const [lostOpen, setLostOpen] = useState(false);

  const update = useUpdateCrmDeal();

  useEffect(() => {
    document.title = deal ? `${deal.title} · Deal` : 'Deal';
  }, [deal]);

  const handleUpdate = (payload: UpdateCrmDealDto): void => {
    const args: UpdateCrmDealArgs = { id, payload };
    update.mutate(args, {
      onSuccess: (updated) => {
        toast.success('Deal atualizado.', { description: updated.title });
        setIsEditing(false);
      },
      onError: (err) =>
        toast.error('Não conseguimos salvar.', {
          description: extractErrorMessage(err, 'Tente de novo em instantes.'),
        }),
    });
  };

  const handleWon = (): void => {
    if (!deal) return;
    update.mutate(
      { id, payload: { version: deal.version, status: 'won' } },
      {
        onSuccess: () => toast.success('Deal marcado como ganho.', { description: deal.title }),
        onError: (err) =>
          toast.error('Não conseguimos marcar como ganho.', {
            description: extractErrorMessage(err, 'Tente de novo em instantes.'),
          }),
      },
    );
  };

  const handleLost = (reason: string): void => {
    if (!deal) return;
    update.mutate(
      { id, payload: { version: deal.version, status: 'lost', lost_reason: reason } },
      {
        onSuccess: () => {
          toast.success('Deal marcado como perdido.', { description: deal.title });
          setLostOpen(false);
        },
        onError: (err) =>
          toast.error('Não conseguimos marcar como perdido.', {
            description: extractErrorMessage(err, 'Tente de novo em instantes.'),
          }),
      },
    );
  };

  if (isLoading) {
    return <DetailSkeleton />;
  }

  if (isError || !deal) {
    return (
      <div className="mx-auto max-w-3xl">
        <div className="rounded-[14px] border border-bad/40 bg-panel p-6">
          <h2 className="text-[15px] font-semibold tracking-[-0.005em] text-ink">
            Não conseguimos carregar o deal.
          </h2>
          <p className="mt-1 text-[12px] text-muted">
            {extractErrorMessage(error, 'O deal pode ter sido removido ou você não tem acesso.')}
          </p>
          <div className="mt-4 flex gap-2">
            <PillButton onClick={() => refetch()} icon={<RefreshCw size={14} strokeWidth={2} />}>
              Tentar de novo
            </PillButton>
            <PillButton onClick={() => navigate('/crm/deals')}>Voltar pra lista</PillButton>
          </div>
        </div>
      </div>
    );
  }

  const isClosed = deal.status !== 'open';

  return (
    <div className="flex flex-col gap-5">
      <header className="flex flex-col gap-3">
        <button
          type="button"
          onClick={() => navigate('/crm/deals')}
          className="inline-flex w-fit items-center gap-1 text-[12px] text-muted transition-colors hover:text-ink"
        >
          <ArrowLeft size={13} strokeWidth={2} />
          Deals
        </button>
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div className="flex flex-col gap-2">
            <h1 className="m-0 text-[26px] font-bold leading-tight tracking-[-0.025em] text-ink">
              {deal.title}
            </h1>
            <div className="flex flex-wrap items-center gap-2 text-[12px] text-muted">
              <span
                className={`inline-flex items-center rounded-[6px] border px-2 py-[2px] ${STATUS_BADGE[deal.status]}`}
              >
                {STATUS_LABEL[deal.status]}
              </span>
              <span className="text-muted-2">{STAGE_LABEL[deal.stage]}</span>
              {!isClosed && (
                <span className="text-muted-3">
                  · {daysInStage(deal.stage_entered_at)} dias no estágio
                </span>
              )}
            </div>
          </div>
          {canWrite && !isClosed && !isEditing && (
            <div className="flex flex-wrap gap-2">
              <PillButton
                onClick={() => setIsEditing(true)}
                icon={<Pencil size={13} strokeWidth={2} />}
              >
                Editar
              </PillButton>
              <PillButton
                onClick={handleWon}
                disabled={update.isPending}
                icon={<Check size={14} strokeWidth={2} />}
              >
                Ganho
              </PillButton>
              <PillButton
                danger
                onClick={() => setLostOpen(true)}
                disabled={update.isPending}
                icon={<X size={14} strokeWidth={2} />}
              >
                Perdido
              </PillButton>
            </div>
          )}
        </div>
      </header>

      {isEditing ? (
        <div className="mx-auto w-full max-w-3xl">
          <DealForm
            initialValues={{
              title: deal.title,
              lead_id: deal.lead_id,
              client_id: deal.client_id,
              saas_product_id: deal.saas_product_id,
              plan_id: deal.plan_id,
              value: deal.value === null ? null : Number(deal.value),
              expected_close_date: deal.expected_close_date,
              responsible_user_id: deal.responsible_user_id,
              origin: deal.origin,
            }}
            responsibleUsers={responsibleUsers ?? []}
            saasProducts={saasProducts?.items ?? []}
            plans={plans?.items ?? []}
            linkedLeadName={deal.lead?.name ?? null}
            linkedClientName={deal.client?.name ?? null}
            isPending={update.isPending}
            submitLabel="Salvar alterações"
            onCancel={() => setIsEditing(false)}
            onSubmit={(values) =>
              handleUpdate({
                ...values,
                version: deal.version,
              } as UpdateCrmDealDto)
            }
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
          <div className="flex flex-col gap-5">
            <DealInfoCard deal={deal} />
          </div>
          <section className="flex flex-col gap-3">
            <h2 className="text-[15px] font-semibold tracking-[-0.005em] text-ink">Atividades</h2>
            <ActivityTimeline target={{ kind: 'deal', id: deal.id }} canWrite={canWrite} />
          </section>
        </div>
      )}

      {lostOpen && (
        <LostDealDialog
          open={lostOpen}
          onOpenChange={setLostOpen}
          dealTitle={deal.title}
          isPending={update.isPending}
          onConfirm={handleLost}
        />
      )}

      {isFetching && !isLoading && (
        <span className="sr-only" aria-live="polite">
          Atualizando…
        </span>
      )}
    </div>
  );
}

// =============================================================================
// Card de dados do deal
// =============================================================================

function DealInfoCard({ deal }: { deal: CrmDealDetailEntity }): JSX.Element {
  const rows: { label: string; value: string }[] = [
    { label: 'Valor', value: formatDealMoney(deal.value) },
    {
      label: 'Fechamento esperado',
      value: deal.expected_close_date ? formatBR(parseLocalDate(deal.expected_close_date)) : '—',
    },
    { label: 'Responsável', value: deal.responsible_user?.name ?? '—' },
    { label: 'Produto SaaS', value: deal.saas_product?.name ?? '—' },
    { label: 'Plano', value: deal.plan?.name ?? '—' },
    { label: 'Lead', value: deal.lead?.name ?? '—' },
    { label: 'Cliente', value: deal.client?.name ?? '—' },
    { label: 'Origem', value: deal.origin ?? '—' },
  ];

  return (
    <section className="rounded-[14px] border border-line bg-panel p-[20px]">
      <h2 className="mb-3 text-[13px] font-semibold tracking-[-0.005em] text-ink">Dados</h2>
      <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
        {rows.map((r) => (
          <div key={r.label} className="flex flex-col gap-[2px]">
            <dt className="text-[11px] uppercase tracking-wider text-muted-3">{r.label}</dt>
            <dd className="m-0 text-[13px] text-ink-2">{r.value}</dd>
          </div>
        ))}
      </dl>

      {deal.status === 'lost' && deal.lost_reason && (
        <div className="mt-4 rounded-[10px] border border-bad/30 bg-bad/5 p-3 text-[12px] text-ink-2">
          <span className="font-medium text-ink">Motivo da perda:</span> {deal.lost_reason}
        </div>
      )}

      {deal.proposals.length > 0 && (
        <div className="mt-4">
          <h3 className="mb-2 text-[11px] uppercase tracking-wider text-muted-3">Propostas</h3>
          <ul className="flex flex-col gap-1">
            {deal.proposals.map((p) => (
              <li key={p.id} className="flex items-center justify-between text-[12px] text-ink-2">
                <span className="font-mono text-muted-2">{p.proposal_number}</span>
                <span className="text-muted">{p.status}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function DetailSkeleton(): JSX.Element {
  return (
    <div className="flex flex-col gap-5">
      <div className="h-4 w-24 animate-pulse rounded bg-panel-2" />
      <div className="h-8 w-72 animate-pulse rounded bg-panel-2" />
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <div className="h-64 animate-pulse rounded-[14px] bg-panel-2" />
        <div className="h-64 animate-pulse rounded-[14px] bg-panel-2" />
      </div>
    </div>
  );
}
